// ChatsPage.tsx
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Paper,
  IconButton,
  TextField,
  CircularProgress,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GlassContainer,
  GlassCard,
  GlassTextField,
  GlassIconButton,
  GlassMessageBubble,
  GlassChatItem,
  GlassChatHeader,
  GlassAttachment,
  SectionHeader,
  BodyText,
} from '../../components/glassmorphism/GlassmorphismStyles';
import { styled } from '@mui/system';
import AttachmentIcon from '@mui/icons-material/Attachment';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import CancelIcon from '@mui/icons-material/Cancel';
import StarIcon from '@mui/icons-material/Star';
import CheckIcon from '@mui/icons-material/Check';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import PreviewImageModal from '../../components/PreviewImage/PreviewImageModal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
  getChats,
  getChatDetails,
  sendMessage,
  searchChats, // ★ NEW
} from './actions';

import {
  ChatDetailsViewModel,
  ChatMessageType,
  SendChatMessageCommand,
} from '../../configuration/api/apiSchema';
import { HandymanAppState } from '../../configuration/redux/reducers';
import { AppDispatch } from '../../App';
import ChatMessageEvent from '../../components/ChatMessageEvent/ChatMessageEvent';
import useDebounce from '../../hooks/UseDebounce';
import { useChatPolling } from '../../hooks/useChatPolling';

/* ───────────────────────── STYLED COMPONENT (REMOVED - Using GlassMessageBubble) ─────────────────────── */

/* ───────────────────────── COMPONENT ────────────────────────────── */
const ChatsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { chatId } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  /* ---------------- Redux state ---------------- */
  const currentUser = useSelector(
    (s: HandymanAppState) => s.user.currentUserDetails,
  );
  const {
    chats,
    searchResults,
    chatDetails,
    loading,
    searchLoading, // ★ NEW
  } = useSelector((s: HandymanAppState) => s.chat);
  const userRole = currentUser?.roleId;

  /* ---------------- Search state ---------------- */
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounce(rawSearch, 500); // ★ NEW

  /* ---------------- Chat polling ---------------- */
  useChatPolling({
    currentChatId: chatId,
    isEnabled: !!currentUser, // Only poll when user is logged in
  });

  /* fire search or fallback list */
  useEffect(() => {
    if (debounced.trim()) {
      dispatch(searchChats(debounced));
    } else if (currentUser) {
      dispatch(getChats());
    }
  }, [debounced, dispatch, currentUser]);

  /* list to display - sorted by updatedAt (newest first) */
  const chatList = useMemo(() => {
    const list = debounced.trim() ? searchResults : chats;
    // Sort by updatedAt timestamp, newest first
    return [...list].sort((a, b) => {
      const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });
  }, [debounced, searchResults, chats]);

  /* ---------------- Other local state ---------------- */
  const [newMessageText, setNewMessageText] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  /* optimized messages array */
  const reversedMessages = useMemo(() => {
    return chatDetails?.messages?.slice().reverse() || [];
  }, [chatDetails?.messages]);

  /* initial load of chats */
  useEffect(() => {
    if (currentUser) dispatch(getChats());
  }, [dispatch, currentUser]);

  /* load messages on chat change */
  useEffect(() => {
    if (chatId) dispatch(getChatDetails(chatId));
  }, [dispatch, chatId]);

  /* ---------------- Helpers ---------------- */
  const fileToBase64 = (file: File) =>
    new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result as string);
      r.onerror = (e) => rej(e);
      r.readAsDataURL(file);
    });

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setAttachmentFile(file);
  };

  const handleSendMessage = async () => {
    if (!newMessageText.trim() && !attachmentFile) return;
    if (!chatId) return;

    let attachmentBase64 = '';
    if (attachmentFile) attachmentBase64 = await fileToBase64(attachmentFile);

    const cmd: SendChatMessageCommand = {
      chatMessageType: ChatMessageType.User,
      content: newMessageText,
      attachmentBase64,
    };

    dispatch(sendMessage(chatId, cmd));
    setNewMessageText('');
    setAttachmentFile(null);
  };

  /* … your existing renderMessage, getChatTitle, skeletons stay unchanged … */

  function getChatTitle(chat: ChatDetailsViewModel, currentUserId?: string) {
    if (chat.title && chat.title.trim() !== '') return chat.title;

    if (chat.participants && chat.participants.length > 0 && currentUserId) {
      if (chat.participants.length === 2) {
        const other = chat.participants.find(
          (p: any) => p.userDetails?.id !== currentUserId,
        );
        if (other?.userDetails?.firstName && other?.userDetails?.lastName) {
          return `${other.userDetails.firstName} ${other.userDetails.lastName}`;
        }
      }
      const names = chat.participants
        .map((p: any) => p.userDetails?.fullName)
        .filter(Boolean)
        .join(', ');
      return names || 'Group Chat';
    }
    return 'No Title';
  }

  const ChatListSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #eee',
          }}
        >
          <Skeleton variant='circular' width={40} height={40} sx={{ mr: 2 }} />
          <Box flexGrow={1}>
            <Skeleton variant='text' width='60%' height={24} />
            <Skeleton variant='text' width='40%' height={20} />
          </Box>
          <Skeleton variant='text' width={40} />
        </Box>
      ))}
    </>
  );

  const ChatMessagesSkeleton = () => (
    <>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: i % 2 === 0 ? 'flex-end' : 'flex-start',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              flexDirection: i % 2 === 0 ? 'row-reverse' : 'row',
            }}
          >
            <Skeleton variant='circular' width={32} height={32} />
            <Skeleton variant='text' width={100} sx={{ mx: 1 }} />
          </Box>
          <Skeleton
            variant='rounded'
            width='60%'
            height={60}
            sx={{
              borderRadius: 2,
              ml: i % 2 === 0 ? 'auto' : 0,
            }}
          />
        </Box>
      ))}
    </>
  );

  const renderMessage = (item: any) => {
    if (item.chatMessageType === ChatMessageType.Event && item.eventDetails) {
      // Get participant names for the event message
      const participants = chatDetails?.participants || [];

      // Find the initiator (sender) of the system message
      const initiatorParticipant = participants.find(
        (p: any) => p.userDetails?.id === item.senderId,
      );

      // Determine if initiator is customer or handyman
      const isInitiatorCustomer =
        initiatorParticipant?.userDetails?.roleId?.toLowerCase() === 'customer';
      const isInitiatorHandyman =
        initiatorParticipant?.userDetails?.roleId?.toLowerCase() === 'handyman';

      // Get initiator's name and avatar
      const getInitiatorName = () => {
        if (initiatorParticipant?.userDetails) {
          const firstName = initiatorParticipant.userDetails.firstName || '';
          const lastName = initiatorParticipant.userDetails.lastName || '';
          const fullName = `${firstName} ${lastName}`.trim();
          if (fullName) return fullName;
        }
        return isInitiatorCustomer
          ? 'Customer'
          : isInitiatorHandyman
            ? 'Handyman'
            : 'User';
      };

      const initiatorName = getInitiatorName();
      const initiatorAvatarUrl =
        initiatorParticipant?.userDetails?.avatarUrl || null;

      return (
        <ChatMessageEvent
          key={item.id}
          event={item.eventDetails}
          roleId={userRole}
          orderStatus={item.eventDetails.orderStatus}
          timestamp={item.timestamp}
          initiatorName={initiatorName}
          initiatorAvatarUrl={initiatorAvatarUrl}
          isInitiatorCustomer={isInitiatorCustomer}
          isInitiatorHandyman={isInitiatorHandyman}
          orderId={item.eventDetails.orderId}
        />
      );
    }

    const isOwn = item.senderId === currentUser?.id;
    const sender = item.sender || (isOwn ? currentUser : {});
    const senderName =
      sender.firstName && sender.lastName
        ? `${sender.firstName} ${sender.lastName}`
        : sender.fullName || 'Unknown';
    const hasAttachment =
      item.attachmentBase64 && item.attachmentBase64.length > 0;

    return (
      <Box
        key={item.id}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          padding: { xs: '12px', md: '16px' },
          gap: { xs: '12px', md: '16px' },
          width: isOwn ? { xs: '95%', sm: 'auto' } : '100%',
          maxWidth: isOwn ? { xs: '95%', sm: '417px' } : '100%',
          alignSelf: isOwn ? 'flex-end' : 'flex-start',
          background: isOwn ? '#005CAD' : isDark ? '#1A1A1A' : '#E5E5DC',
          boxShadow:
            '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
          borderRadius: isOwn
            ? { xs: '24px 24px 8px 24px', md: '32px 32px 12px 32px' }
            : { xs: '24px 24px 24px 8px', md: '32px 32px 32px 12px' },
          marginBottom: '8px',
        }}
      >
        <Avatar
          src={sender.avatarUrl || undefined}
          alt={`${sender.firstName} ${sender.lastName}` || 'Sender'}
          sx={{
            width: { xs: '32px', md: '40px' },
            height: { xs: '32px', md: '40px' },
            boxShadow:
              '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
            borderRadius: '100px',
            fontSize: { xs: '14px', md: '16px' },
          }}
        >
          {!sender.avatarUrl && (sender.firstName?.charAt(0) || '?')}
        </Avatar>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: '4px',
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '14px', md: '16px' },
                lineHeight: '175%',
                letterSpacing: '0.15px',
                color: isOwn ? '#F4F4F4' : isDark ? '#B6B6B6' : '#1A1A1A',
                flex: 1,
              }}
            >
              {senderName}
            </Typography>
            <Box
              sx={{
                width: '16px',
                height: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
                <path
                  d='M8 1.33L9.67 6.33L14.67 8L9.67 9.67L8 14.67L6.33 9.67L1.33 8L6.33 6.33L8 1.33Z'
                  fill={isOwn ? '#00FFFF' : '#005CAD'}
                />
              </svg>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'baseline',
              gap: '8px',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '143%',
                letterSpacing: '0.17px',
                color: isOwn ? '#D5D5D5' : isDark ? '#D5D5D5' : '#585858',
                flex: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {item.content || ''}
            </Typography>
          </Box>

          {hasAttachment && (
            <Box
              sx={{
                mt: 1,
                padding: 0,
                borderRadius: '12px',
                overflow: 'hidden',
                maxWidth: '100%',
              }}
            >
              <img
                src={item.attachmentBase64}
                alt='attachment'
                style={{
                  cursor: 'pointer',
                  maxWidth: '100%',
                  maxHeight: 200,
                  borderRadius: '12px',
                  display: 'block',
                }}
                onClick={() => {
                  setSelectedImage(item.attachmentBase64);
                  setIsImageModalOpen(true);
                }}
              />
            </Box>
          )}
        </Box>

        {isOwn && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
              gap: '16px',
              width: '36px',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '143%',
                letterSpacing: '0.17px',
                color: '#D5D5D5',
              }}
            >
              {new Date(item.timestamp).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </Typography>
            <CheckIcon
              sx={{ width: '16px', height: '16px', color: '#00FFFF' }}
            />
          </Box>
        )}
      </Box>
    );
  };

  /* ───────────────────────── JSX ───────────────────────── */
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: { xs: '16px', md: '24px' },
        paddingTop: { xs: '80px', md: '120px' },
        gap: { xs: '24px', md: '48px' },
        background: isDark ? '#1A1A1A' : '#E5E5DC',
      }}
    >
      <PreviewImageModal
        base64SelectedImage={selectedImage}
        isImageModalOpen={isImageModalOpen}
        handleCloseImageModal={() => {
          setIsImageModalOpen(false);
          setSelectedImage(null);
        }}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          width: '100%',
          maxWidth: '1152px',
          height: { xs: 'calc(100vh - 120px)', md: '816px' },
          background: isDark ? '#1A1A1A' : '#D1D0C6',
          boxShadow:
            '10px 10px 28.6642px -11.7px rgba(0, 0, 0, 0.2), -17.5358px -17.5358px 28.6642px rgba(255, 255, 255, 0.05), 14.1509px 14.1509px 22.6415px -0.373585px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3), inset -2.0717px -2.63774px 4.52338px -1.50779px rgba(0, 0, 0, 0.3), inset 1.57925px 1.57925px 1.69811px #FFFFFF',
          borderRadius: { xs: '24px', md: '32px' },
          overflow: 'hidden',
        }}
      >
        {/* ─────────── LEFT SIDEBAR ─────────── */}
        <Box
          sx={{
            display: { xs: chatId ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: { xs: '24px 16px', md: '40px 32px' },
            gap: '8px',
            width: { xs: '100%', md: '400px' },
            minWidth: { xs: '100%', md: '400px' },
            height: '100%',
            background: isDark ? '#1A1A1A' : '#E5E5DC',
            boxShadow:
              '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
          }}
        >
          {/* Chats Header & Search */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '24px',
              width: '100%',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '20px', md: '24px' },
                lineHeight: '133.4%',
                textAlign: 'center',
                color: isDark ? '#00FFFF' : '#005CAD',
              }}
            >
              Chats
            </Typography>

            {/* Search Input */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '2px 2px 2px 16px',
                gap: '8px',
                width: '100%',
                minHeight: '36px',
                background: 'rgba(153, 153, 153, 0.3)',
                backgroundBlendMode: 'darken',
                boxShadow:
                  '0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), 0px 0px 34.773px rgba(0, 0, 0, 0.2), inset 0px 1.07796px 2.48379px #000000, inset 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4)',
                borderRadius: '64px',
              }}
            >
              <input
                type='text'
                placeholder='Search chats'
                value={rawSearch}
                onChange={(e) => setRawSearch(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontFamily: "'Montserrat', sans-serif",
                  fontWeight: 500,
                  fontSize: '12px',
                  lineHeight: '24px',
                  color: isDark ? '#D5D5D5' : '#1A1A1A',
                }}
              />
              <Box
                component='button'
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '4px 12px',
                  gap: '8px',
                  height: '32px',
                  background: isDark
                    ? 'rgba(0, 92, 173, 0.7)'
                    : 'rgba(0, 36, 58, 0.8)',
                  boxShadow:
                    '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                  backdropFilter: 'blur(6px)',
                  borderRadius: '64px',
                  border: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.85)'
                      : 'rgba(0, 36, 58, 0.9)',
                  },
                }}
              >
                {searchLoading ? (
                  <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
                ) : (
                  <Typography
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 500,
                      fontSize: '12px',
                      lineHeight: '24px',
                      color: '#FFFFFF',
                    }}
                  >
                    Search
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          {/* Chat List */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              padding: '24px 16px',
              width: '100%',
              flex: 1,
              overflowY: 'auto',
              gap: '0px',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: isDark
                  ? 'rgba(0, 92, 173, 0.5)'
                  : 'rgba(0, 36, 58, 0.3)',
                borderRadius: '3px',
              },
            }}
          >
            {loading && !debounced.trim() ? (
              <ChatListSkeleton />
            ) : (
              chatList.map((chat, index) => {
                const isActive = chatId === chat.id;
                return (
                  <Box
                    key={chat.id}
                    onClick={() => navigate(`/chats/${chat.id}`)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      padding: '16px',
                      gap: '16px',
                      width: '100%',
                      minHeight: '84px',
                      background: isActive
                        ? isDark
                          ? 'rgba(0, 92, 173, 0.2)'
                          : '#E5E5DC'
                        : 'transparent',
                      boxShadow: isActive
                        ? 'inset 5px 5px 24px -11.7px rgba(0, 181, 181, 0.49), inset -7.54px -7.54px 10px rgba(222, 255, 255, 0.39)'
                        : 'none',
                      borderRadius: '32px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.15)'
                          : 'rgba(229, 229, 220, 0.5)',
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: { xs: '36px', md: '40px' },
                        height: { xs: '36px', md: '40px' },
                        fontSize: { xs: '14px', md: '16px' },
                        boxShadow:
                          '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                        borderRadius: '100px',
                      }}
                      src={
                        chat.participants?.find(
                          (p: any) => p.userDetails?.id !== currentUser?.id,
                        )?.userDetails?.avatarUrl || undefined
                      }
                    >
                      {getChatTitle(chat, currentUser?.id).charAt(0)}
                    </Avatar>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '4px',
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '8px',
                          width: '100%',
                        }}
                      >
                        <Typography
                          noWrap
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 700,
                            fontSize: { xs: '14px', md: '16px' },
                            lineHeight: '175%',
                            letterSpacing: '0.15px',
                            color: isDark ? '#B6B6B6' : '#1A1A1A',
                            flex: 1,
                          }}
                        >
                          {getChatTitle(chat, currentUser?.id)}
                        </Typography>
                        <Box
                          sx={{
                            width: '16px',
                            height: '16px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <svg
                            width='16'
                            height='16'
                            viewBox='0 0 16 16'
                            fill='none'
                          >
                            <path
                              d='M8 1.33L9.67 6.33L14.67 8L9.67 9.67L8 14.67L6.33 9.67L1.33 8L6.33 6.33L8 1.33Z'
                              fill='#005CAD'
                            />
                          </svg>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                          gap: '8px',
                          width: '100%',
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: { xs: '12px', md: '14px' },
                            lineHeight: '143%',
                            letterSpacing: '0.17px',
                            color: isDark ? '#D5D5D5' : '#585858',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {chat.lastMessage || 'No messages'}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: "'Roboto', sans-serif",
                            fontWeight: 400,
                            fontSize: { xs: '11px', md: '14px' },
                            lineHeight: '143%',
                            letterSpacing: '0.17px',
                            color: isDark ? '#D5D5D5' : '#585858',
                            flexShrink: 0,
                          }}
                        >
                          {chat.updatedAt &&
                            new Date(chat.updatedAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* ─────────── RIGHT PANE ─────────── */}
        <Box
          sx={{
            display: { xs: chatId ? 'flex' : 'none', md: 'flex' },
            flex: 1,
            width: { xs: '100%', md: 'auto' },
            height: '100%',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          {chatId ? (
            <>
              {/* Chat header */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  padding: { xs: '16px', sm: '24px', md: '40px 32px' },
                  gap: { xs: '12px', md: '16px' },
                  width: '100%',
                  minHeight: { xs: '80px', md: '136px' },
                  background: isDark ? '#1A1A1A' : '#E5E5DC',
                  borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                }}
              >
                <IconButton
                  onClick={() => navigate('/chats')}
                  sx={{
                    display: { xs: 'flex', md: 'none' },
                    padding: '8px',
                    color: isDark ? '#00FFFF' : '#005CAD',
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Avatar
                  sx={{
                    width: '40px',
                    height: '40px',
                    boxShadow:
                      '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
                    borderRadius: '100px',
                  }}
                  src={
                    chatDetails?.participants?.find(
                      (p: any) => p.userDetails?.id !== currentUser?.id,
                    )?.userDetails?.avatarUrl || undefined
                  }
                >
                  {chatDetails &&
                    getChatTitle(chatDetails, currentUser?.id).charAt(0)}
                </Avatar>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '4px',
                    flex: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontWeight: 800,
                      fontSize: { xs: '18px', md: '20px' },
                      lineHeight: '160%',
                      letterSpacing: '0.15px',
                      color: isDark ? '#B6B6B6' : '#1A1A1A',
                    }}
                  >
                    {chatDetails && getChatTitle(chatDetails, currentUser?.id)}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "'Roboto', sans-serif",
                      fontWeight: 400,
                      fontSize: { xs: '12px', md: '14px' },
                      lineHeight: '143%',
                      letterSpacing: '0.17px',
                      color: isDark ? '#D5D5D5' : '#585858',
                    }}
                  >
                    Online at {new Date().toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>

              {/* Messages */}
              <Box
                sx={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  display: 'flex',
                  flexDirection: 'column-reverse',
                  padding: {
                    xs: '16px 16px 90px 16px',
                    md: '24px 32px 100px 32px',
                  },
                  gap: { xs: '12px', md: '16px' },
                  background: 'transparent',
                  WebkitOverflowScrolling: 'touch',
                  '&::-webkit-scrollbar': {
                    width: '6px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: isDark
                      ? 'rgba(0, 92, 173, 0.5)'
                      : 'rgba(0, 36, 58, 0.3)',
                    borderRadius: '3px',
                  },
                }}
              >
                {loading ? (
                  <ChatMessagesSkeleton />
                ) : (
                  reversedMessages.map((msg) => renderMessage(msg))
                )}
              </Box>

              {/* Composer */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: { xs: '12px', md: '16px' },
                  gap: { xs: '6px', md: '8px' },
                  width: '100%',
                  minHeight: { xs: '60px', md: '72px' },
                  background: 'rgba(153, 153, 153, 0.3)',
                  backgroundBlendMode: 'darken',
                  boxShadow:
                    '0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), 0px 0px 34.773px rgba(0, 0, 0, 0.2), inset 0px 1.07796px 2.48379px #000000, inset 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4)',
                }}
              >
                {attachmentFile && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '-60px',
                      left: '16px',
                      p: 1,
                      borderRadius: 1,
                      bgcolor: isDark
                        ? 'rgba(0, 0, 0, 0.3)'
                        : 'rgba(255, 255, 255, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <img
                      src={URL.createObjectURL(attachmentFile)}
                      alt='attachment preview'
                      style={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 4,
                      }}
                    />
                    <Typography
                      variant='body2'
                      sx={{
                        flexGrow: 1,
                        color: isDark ? '#D5D5D5' : '#1A1A1A',
                      }}
                    >
                      {attachmentFile.name}
                    </Typography>
                    <IconButton
                      size='small'
                      onClick={() => setAttachmentFile(null)}
                      sx={{ color: isDark ? '#D5D5D5' : '#1A1A1A' }}
                    >
                      <CancelIcon fontSize='small' />
                    </IconButton>
                  </Box>
                )}

                <input
                  type='text'
                  placeholder='Message text'
                  value={newMessageText}
                  onChange={(e) => setNewMessageText(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    outline: 'none',
                    fontFamily: "'Montserrat', sans-serif",
                    fontWeight: 500,
                    fontSize: '12px',
                    lineHeight: '24px',
                    color: isDark ? '#D5D5D5' : '#1A1A1A',
                    padding: '0 8px',
                  }}
                />

                <Box
                  component='button'
                  onClick={handleSendMessage}
                  disabled={!newMessageText.trim() && !attachmentFile}
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: { xs: '8px', md: '10px' },
                    gap: '8px',
                    width: { xs: '36px', md: '40px' },
                    height: { xs: '36px', md: '40px' },
                    background: 'rgba(153, 153, 153, 0.3)',
                    backgroundBlendMode: 'darken',
                    boxShadow:
                      '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                    backdropFilter: 'blur(6px)',
                    borderRadius: '64px',
                    border: 'none',
                    cursor:
                      !newMessageText.trim() && !attachmentFile
                        ? 'not-allowed'
                        : 'pointer',
                    opacity:
                      !newMessageText.trim() && !attachmentFile ? 0.5 : 1,
                    '&:hover': {
                      background:
                        !newMessageText.trim() && !attachmentFile
                          ? 'rgba(153, 153, 153, 0.3)'
                          : 'rgba(153, 153, 153, 0.4)',
                    },
                  }}
                >
                  <SendIcon
                    sx={{
                      width: { xs: '16px', md: '20px' },
                      height: { xs: '16px', md: '20px' },
                      color: '#E2F7FF',
                    }}
                  />
                </Box>

                <label>
                  <Box
                    component='button'
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: { xs: '8px', md: '10px' },
                      gap: '8px',
                      width: { xs: '36px', md: '40px' },
                      height: { xs: '36px', md: '40px' },
                      background: isDark
                        ? 'rgba(0, 92, 173, 0.7)'
                        : 'rgba(0, 36, 58, 0.8)',
                      boxShadow:
                        '0px 0px 2px rgba(0, 0, 0, 0.1), 0px 1px 8px rgba(0, 0, 0, 0.12), inset 1px 1px 0px -0.5px #333333, inset -1px -1px 0px -0.5px #262626, inset 1px 1px 0.5px -1px #FFFFFF, inset -1px -1px 0.5px -1px #FFFFFF, inset 0px 0px 3px rgba(255, 255, 255, 0.5), inset 0px 0px 16px #F2F2F2',
                      backdropFilter: 'blur(6px)',
                      borderRadius: '64px',
                      border: 'none',
                      cursor: 'pointer',
                      '&:hover': {
                        background: isDark
                          ? 'rgba(0, 92, 173, 0.85)'
                          : 'rgba(0, 36, 58, 0.9)',
                      },
                    }}
                  >
                    <AttachmentIcon
                      sx={{
                        width: { xs: '16px', md: '20px' },
                        height: { xs: '16px', md: '20px' },
                        color: '#E2F7FF',
                      }}
                    />
                  </Box>
                  <input
                    type='file'
                    hidden
                    onChange={handleAttachmentChange}
                    accept='image/*'
                  />
                </label>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
                background: isDark ? '#1A1A1A' : '#E5E5DC',
              }}
            >
              <ChatIcon
                sx={{ fontSize: 64, color: isDark ? '#B6B6B6' : '#585858' }}
              />
              <Typography
                sx={{
                  fontFamily: "'Roboto', sans-serif",
                  fontWeight: 400,
                  fontSize: '16px',
                  color: isDark ? '#B6B6B6' : '#585858',
                }}
              >
                Select a chat to start messaging
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatsPage;
